import {Box, Typography, Icon} from '@mui/material';
import {FC, useEffect, useState} from 'react';
import {getErrorMessage} from '@/utils/helpers';
import {useAppDispatch} from '@/store/hooks';
import {toast} from 'react-toastify';
import {ReactComponent as SecurityIcon} from '@icons/security-safe.svg';
import {superAdminLogin2Factor} from '@/store/admin';
import {OUTLINE} from '@/utils/constants/button';
import Button from '@/shared/components/Button';
import {CodeForm} from '@/pages/Admin/Profile/General/TwoStepAuthentication/CodeForm';
import {twoFactorLoginFormStyles} from './TwoFactorLoginForm.styles';

interface TwoFactorLoginFormProps {
  challenge: {challengeToken: string; expiresAt: string};
  onVerified: () => void;
  onBack: () => void;
}

const TwoFactorLoginForm: FC<TwoFactorLoginFormProps> = ({challenge, onVerified, onBack}) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);

  const codeValue = code.join('');
  const isCodeComplete = /^\d{6}$/.test(codeValue);

  // challenge має термін дії
  useEffect(() => {
    const ms = new Date(challenge.expiresAt).getTime() - Date.now();
    const timer = setTimeout(() => {
      toast.error('Verification time expired. Please sign in again.');
      onBack();
    }, Math.max(ms, 0));
    return () => clearTimeout(timer);
  }, [challenge.expiresAt]);

  const handleVerify = async () => {
    setIsLoading(true);
    try {
      await dispatch(superAdminLogin2Factor({challengeToken: challenge.challengeToken, code: codeValue})).unwrap();
      onVerified();
    } catch (error) {
      toast.error(getErrorMessage(error));
      setCode(Array(6).fill(''));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Icon sx={twoFactorLoginFormStyles.icon}>
        <SecurityIcon />
      </Icon>
      <Typography sx={{textAlign: 'center', marginTop: '32px', fontSize: '24px', fontWeight: '700'}}>Two-Step Verification</Typography>
      <Typography sx={{marginTop: '24px'}}>Enter the 6-digit code from your authentication app.</Typography>
      <CodeForm code={code} handleCode={setCode} />
      <Box sx={twoFactorLoginFormStyles.buttonWrapper}>
        <Button title="Back" variant={OUTLINE} onClick={onBack} />
        <Button title="Verify" onClick={handleVerify} disabled={!isCodeComplete || isLoading} />
      </Box>
    </Box>
  );
};

export default TwoFactorLoginForm;
